import React from 'react';
import { Box, HStack, Select, Text, VStack } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { AreaGraph } from 'src/components/molecules/graphs/AreaGraph';
import { LegendContextProvider } from 'src/components/molecules/Legend/LegendContext/LegendContextProvider';
import { DataSet } from 'src/features/DataUpload/types';
import { snakeToTitleCase } from 'src/utils/String';
import { useCurrency } from 'src/hooks/useCurrency';
import {
  calculateTotal,
  formatChartData,
  formatTotal,
  formatYAxis,
  getTimePeriod,
} from 'src/features/DataUpload/utils/dataVisualizationHelpers';

type DataSetPanelProps = {
  dataSet: DataSet;
  isExpanded: boolean;
  isLastItem: boolean;
  onToggle: (id: string) => void;
  availableMetrics: Record<string, string[]>;
  availableMediaSources: Record<string, string[]>;
  selectedMetrics: Record<string, string>;
  selectedMediaSources: Record<string, string>;
  onMetricChange: (
    dataType: string,
  ) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onMediaSourceChange: (
    dataType: string,
  ) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const DataSetPanel: React.FC<DataSetPanelProps> = ({
  dataSet,
  isExpanded,
  isLastItem,
  onToggle,
  availableMetrics,
  availableMediaSources,
  selectedMetrics,
  selectedMediaSources,
  onMetricChange,
  onMediaSourceChange,
}) => {
  const { code: currencyCode } = useCurrency();
  const dataTypeKey = dataSet.id.replace('-data', '');
  const currentMetric = selectedMetrics[dataTypeKey] || '';
  const formattedMetricLabel = currentMetric
    ? currentMetric.charAt(0).toUpperCase() + currentMetric.slice(1)
    : '';
  const chartData = formatChartData(dataSet.data);
  const mediaSources = availableMediaSources[dataTypeKey] || [];
  const metricOptions = availableMetrics[dataTypeKey] || [];
  const timePeriod = getTimePeriod(chartData);

  return (
    <Box>
      <Box
        p={4}
        bg={isExpanded ? 'surface.sidebarMuted' : 'transparent'}
        borderTopRadius="12px"
        border={isExpanded ? '1px solid' : 'none'}
        borderBottom="none"
        borderColor={isExpanded ? 'surface.border' : 'transparent'}
        cursor="pointer"
        onClick={() => onToggle(dataSet.id)}
        transition="all 0.2s">
        <HStack spacing={3} align="center">
          <Icon
            iconName={isExpanded ? 'chevron-down' : 'chevron-right'}
            w="16px"
            h="16px"
            color="content.muted"
          />
          <Text fontSize="md" fontWeight={500} color="content.body">
            {dataSet.name}
          </Text>
        </HStack>
      </Box>

      {isExpanded && (
        <Box
          px={3}
          py={4}
          bg="surface.sidebarMuted"
          border="1px solid"
          borderTop="none"
          borderColor="surface.border"
          borderBottomRadius="12px">
          <Box
            mt={2}
            p={6}
            bg="surface.panel"
            borderRadius="8px"
            border="1px solid"
            borderColor="surface.border">
            <VStack spacing={4} align="stretch" mb={6}>
              <HStack justify="space-between" align="center">
                <Text fontSize="lg" fontWeight={600} color="content.title">
                  Data
                </Text>
                <HStack spacing={3}>
                  {dataSet.id !== 'kpi-data' && mediaSources.length > 0 && (
                    <Select
                      w="216px"
                      h="44px"
                      variant="filled"
                      borderRadius="8px"
                      fontSize="sm"
                      bg="surface.sidebarMuted"
                      value={selectedMediaSources[dataTypeKey]}
                      onChange={onMediaSourceChange(dataTypeKey)}>
                      <option value="all">All Media/Sources</option>
                      {mediaSources.map((mediaSource) => (
                        <option key={mediaSource} value={mediaSource}>
                          {mediaSource}
                        </option>
                      ))}
                    </Select>
                  )}
                  <Select
                    w="216px"
                    h="44px"
                    variant="filled"
                    borderRadius="8px"
                    fontSize="sm"
                    bg="surface.sidebarMuted"
                    value={currentMetric}
                    onChange={onMetricChange(dataTypeKey)}>
                    {metricOptions.map((metric) => (
                      <option key={metric} value={metric}>
                        {snakeToTitleCase(metric)}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </HStack>
            </VStack>

            <Box h="400px" w="100%">
              <LegendContextProvider
                keyColorMap={{ value: 'green' }}
                lineKeys={['value']}>
                <AreaGraph
                  data={chartData}
                  xAxisKey="date"
                  xAxisFormatter={(value) => value.toString()}
                  yAxisFormatter={(value) =>
                    formatYAxis(
                      value,
                      dataSet.id,
                      selectedMetrics,
                      currencyCode,
                    )
                  }
                />
              </LegendContextProvider>
            </Box>

            <Box mt={4} p={4} bg="surface.sidebarMuted" borderRadius="8px">
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" align="center">
                  <Text fontSize="md" fontWeight={500} color="content.label">
                    {`Total ${formattedMetricLabel}:`}
                  </Text>
                  <Text fontSize="xl" fontWeight={700} color="content.title">
                    {formatTotal(
                      calculateTotal(chartData),
                      dataSet.id,
                      selectedMetrics,
                      currencyCode,
                    )}
                  </Text>
                </HStack>
                {timePeriod && (
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight={400} color="content.muted">
                      Time Period:
                    </Text>
                    <Text fontSize="sm" fontWeight={500} color="content.label">
                      {timePeriod.startDate} - {timePeriod.endDate}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>
          </Box>
        </Box>
      )}

      {!isLastItem && <Box h="1px" bg="surface.border" mt={3} />}
    </Box>
  );
};
